import {
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService, Toast } from '../../../services/toast.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './toast.component.html',
})
export class ToastComponent implements OnInit, OnDestroy {
  toasts: Toast[] = [];

  private sub?: Subscription;
  private visibleMap = new Map<string, boolean>();
  private autoCloseTimeouts = new Map<string, number>();
  private closingToasts = new Set<string>();
  constructor(private toastService: ToastService) {}

  ngOnInit(): void {
    this.sub = this.toastService.toasts$.subscribe((toasts) => {
      this.syncToasts(toasts);
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.autoCloseTimeouts.forEach((id) => clearTimeout(id));
    this.autoCloseTimeouts.clear();
    this.closingToasts.clear();
  }

  private syncToasts(toasts: Toast[]): void {
    this.toasts = toasts;

    for (const toast of toasts) {
      if (!this.visibleMap.has(toast.id) && !this.closingToasts.has(toast.id)) {
        this.visibleMap.set(toast.id, false);

        // enter animation
        setTimeout(() => {
          // Double-check toast still exists and isn't closing
          if (this.toasts.some(t => t.id === toast.id) && !this.closingToasts.has(toast.id)) {
            this.visibleMap.set(toast.id, true);
          }
        });

        // auto-dismiss
        if (toast.duration && toast.duration > 0) {
          const timeoutId = window.setTimeout(() => {
            this.startClose(toast.id);
          }, toast.duration);
          this.autoCloseTimeouts.set(toast.id, timeoutId);
        }
      }
    }

    const ids = new Set(toasts.map((t) => t.id));
    Array.from(this.visibleMap.keys()).forEach((id) => {
      if (!ids.has(id)) {
        this.visibleMap.delete(id);
        this.closingToasts.delete(id);
      }
    });
  }

  isVisible(id: string): boolean {
    return this.visibleMap.get(id) ?? false;
  }

  onManualClose(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.startClose(id);
  }

  private startClose(id: string): void {
    // Prevent duplicate close operations
    if (this.closingToasts.has(id)) return;
    if (!this.visibleMap.get(id)) return;
    
    // Mark as closing to prevent re-animation
    this.closingToasts.add(id);
    this.visibleMap.set(id, false);

    const timeoutId = this.autoCloseTimeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.autoCloseTimeouts.delete(id);
    }

    setTimeout(() => {
      this.toastService.remove(id);
      this.closingToasts.delete(id);
    }, 250);
  }

  /** Icon ring color + icon color */
  getIconRingClass(type: Toast['type']): string {
    switch (type) {
      case 'success':
        return 'border-emerald-500 text-emerald-500';
      case 'error':
        return 'border-rose-500 text-rose-500';
      case 'warning':
        return 'border-amber-500 text-amber-500';
      case 'info':
      default:
        return 'border-sky-500 text-sky-500';
    }
  }

  /** Icon shape */
  getIconClass(type: Toast['type']): string {
    switch (type) {
      case 'success':
        return 'bi-check-lg';
      case 'error':
        return 'bi-x-circle';
      case 'warning':
        return 'bi-exclamation-lg';
      case 'info':
      default:
        return 'bi-info-lg';
    }
  }

  getBorderLeftClass(type: Toast['type']): string {
    switch (type) {
      case 'success':
        return 'border-emerald-400';
      case 'error':
        return 'border-red-400';
      case 'warning':
        return 'border-amber-400';
      case 'info':
      default:
        return 'border-sky-400';
    }
  }
  
}
